import React, { createContext, useContext, useState, useEffect } from 'react';
import { THEMES_CONFIG, DEFAULT_THEME } from '../config/themes';
import { getHolidayTheme, getHolidaySchedule } from '../config/holidaySchedule';

// Всі можливі святкові теми (незалежно від дати)
const ALL_HOLIDAY_THEMES = new Set(
  getHolidaySchedule(new Date().getFullYear()).map(h => h.theme)
);
const isHolidayThemeName = (t) => ALL_HOLIDAY_THEMES.has(t);

export const THEMES = THEMES_CONFIG;

// Safe localStorage — кидає SecurityError в Safari Private Mode
function lsGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function lsSet(key, val) { try { localStorage.setItem(key, val); } catch {} }
function lsRemove(key) { try { localStorage.removeItem(key); } catch {} }

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEMES_CONFIG,
  isAutoTheme: false,
});

// Повертає базову тему сайту (Color Web або orange)
const getBaseTheme = () => {
  const colorWeb = window._bvS?.forceTheme;
  return (colorWeb && colorWeb !== '' && THEMES_CONFIG[colorWeb]) ? colorWeb : DEFAULT_THEME;
};

// Повертає поточну авто-тему: свято → святкова, інакше → base
const getAutoTheme = () => {
  const autoDisabled = window._bvS?.autoTheme === 'FALSE' || window._bvS?.autoTheme === false;
  const holidayOff   = lsGet('bv_holiday_off') === 'true';
  if (!autoDisabled && !holidayOff) {
    const holiday = getHolidayTheme();
    if (holiday && THEMES_CONFIG[holiday]) return holiday;
  }
  return getBaseTheme();
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const saved    = lsGet('bv_theme');
    const isManual = lsGet('bv_theme_manual') === 'true';

    if (isManual && saved && THEMES_CONFIG[saved]) {
      const currentHoliday = getHolidayTheme();
      // Якщо збережена тема — свято, а зараз свята нема → скидаємо
      if (isHolidayThemeName(saved) && !currentHoliday) {
        lsRemove('bv_theme_manual');
        return getAutoTheme();
      }
      return saved;
    }

    return getAutoTheme();
  });

  const [isAutoTheme, setIsAutoTheme] = useState(() => {
    return lsGet('bv_theme_manual') !== 'true';
  });

  // Ручна зміна теми юзером
  const setTheme = (themeId) => {
    if (!THEMES_CONFIG[themeId]) return;
    setThemeState(themeId);
    lsSet('bv_theme', themeId);
    lsSet('bv_theme_manual', 'true');
    setIsAutoTheme(false);
  };

  // Скинути до авто (свято або base)
  const setAutoTheme = () => {
    lsRemove('bv_theme_manual');
    const auto = getAutoTheme();
    setThemeState(auto);
    lsSet('bv_theme', auto);
    setIsAutoTheme(true);
  };

  // Реагуємо на оновлення settings та зміну Color Web
  useEffect(() => {
    const apply = () => {
      if (!window._bvS) return;

      const pickerEnabled = window._bvS?.showThemePicker === 'TRUE' || window._bvS?.showThemePicker === true;
      const isManual = lsGet('bv_theme_manual') === 'true';
      const savedTheme = lsGet('bv_theme');

      // Якщо picker вимкнений і юзер вибирав вручну — скидаємо до авто/base
      if (isManual && !pickerEnabled) {
        lsRemove('bv_theme_manual');
        const auto = getAutoTheme();
        setThemeState(auto);
        lsSet('bv_theme', auto);
        setIsAutoTheme(true);
        return;
      }

      // Якщо юзер вручну вибрав святкову тему але свято вже скінчилось — скидаємо на авто
      if (isManual && savedTheme) {
        const currentHoliday = getHolidayTheme();
        if (isHolidayThemeName(savedTheme) && !currentHoliday) {
          lsRemove('bv_theme_manual');
          const auto = getAutoTheme();
          setThemeState(auto);
          lsSet('bv_theme', auto);
          setIsAutoTheme(true);
          return;
        }
      }

      // Якщо юзер в авто-режимі — оновлюємо тему (могла змінитись Color Web)
      if (!isManual) {
        const auto = getAutoTheme();
        setThemeState(auto);
        lsSet('bv_theme', auto);
      }
    };

    window.addEventListener('bv_settings_updated', apply);
    return () => window.removeEventListener('bv_settings_updated', apply);
  }, []);

  useEffect(() => {
    // Адмін-панель має свою тему — не перезаписуємо
    if (window.location.pathname.startsWith('/fanbox')) return;
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, setAutoTheme, themes: THEMES_CONFIG, isAutoTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
